import "react-loading-skeleton/dist/skeleton.css";
import { FC, useEffect, useMemo, useState } from "react";
import { Purchase, store } from "@cleeng/mediastore-sdk";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { useGetOfferQuery } from "@features/ottPurchase/services/ottPurchaseApi";

import { useLocale } from "@/shared/hooks/useLocale";
import { Provider } from "react-redux";
import { OTTPurchaseWrapper } from "@features/ottPurchase/elements/OTTPurchaseWrapper";
import { ThemeProvider } from "styled-components";
import { useThemeColor } from "@features/ottPurchase/hooks/useThemeColor";
import { darkTheme, defaultTheme } from "@motain/web-shared-hub/themes";
import { selectIsDarkTheme } from "@motain/web-shared-hub/services";
import {
  LinkWithLang,
  LogoLoadingSpinnerCentered,
} from "@motain/web-shared-hub/ui/blocks";
import { ScreenName } from "@motain/web-shared-hub/features";
import { usePageTracking } from "@features/tracking/hooks/usePageTracking";
import { useScreenName } from "@features/tracking/hooks/useScreenName";
import { is } from "superstruct";
import {
  EnvironmentStruct,
  GetOfferErrorResponseStruct,
} from "@features/ottPurchase/types/GetOfferApi";
import { DownloadAppBanner } from "@ui/blocks/DownloadAppBanner";
import { useAppSelector } from "@/shared/hooks/appHooks";
import { setupConfig } from "@features/ottPurchase/helpers/setupCleengConfig";
import { BodyText } from "@motain/web-shared-hub/ui/elements";
import { OTTInfoMessage } from "@features/ottPurchase/elements/OTTInfoMessage";
import { FontSize } from "@motain/web-shared-hub/types";
import { AppRoutes } from "@/shared/constants/Routes";
import { useRedirectUrl } from "@/shared/hooks/useRedirectUrl";
import { isNullish } from "@motain/web-shared-hub/helpers";

const CLEENG_UNSUPPORTED_REGION_ERROR_TYPE = "cleeng_unsupported_region";
const TERMS_AND_CONDITIONS_URL =
  "https://company.onefootball.com/terms-and-conditions/";

const CURRENCIES_TO_OMIT_INFO_MESSAGE = ["EUR", "USD"];

export const OTTPurchaseContainer: FC = () => {
  useScreenName(ScreenName.CheckoutPage);
  usePageTracking();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUrl = useRedirectUrl();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const env = searchParams.get("env");
  const { locale, t } = useLocale();

  const { data, isLoading, isError, error, isSuccess } = useGetOfferQuery({
    offerId: params.offerId || "", // -> url
    locale: params.lang || "en",
    environment: is(env, EnvironmentStruct) ? env : undefined,
  });

  const shouldShowInfoMessage = useMemo(
    () =>
      !isNullish(data?.data.currency) &&
      isSuccess &&
      !CURRENCIES_TO_OMIT_INFO_MESSAGE.includes(data.data.currency),
    [data?.data.currency, isSuccess],
  );
  const themeColor = useThemeColor();
  const [shouldShowPurchase, setShouldShowPurchase] = useState(false);
  const isDarkMode = useAppSelector(selectIsDarkTheme);

  const ThemeColor = isDarkMode ? darkTheme : defaultTheme;
  const styleObject = {
    base: {
      color: ThemeColor.colors.bodyText,
      background: "transparent",
    },
    error: {
      color: ThemeColor.colors.bodyText,
      background: "transparent",
    },
    placeholder: {
      color: ThemeColor.colors.bodyText,
      background: "transparent",
    },
    validated: {
      color: ThemeColor.colors.bodyText,
      background: "transparent",
    },
  };

  useEffect(() => {
    if (isNullish(data) || isError || !isSuccess) {
      return;
    }

    setupConfig({
      accessToken: data.data.access_token,
      locale,
      offerId: params.offerId ?? "",
      env: is(env, EnvironmentStruct) ? env : undefined,
      redirectUrl,
      cancelUrl: location.pathname,
    });
    setShouldShowPurchase(true);
  }, [
    data,
    isError,
    isSuccess,
    locale,
    env,
    redirectUrl,
    location.pathname,
    params.offerId,
  ]);

  if (isError && is(error, GetOfferErrorResponseStruct)) {
    return (
      <DownloadAppBanner
        title={error.error.title}
        message={error.error.message}
        getAppText={t("GET_THE_APP")}
        shouldShowDownloadIcons={
          error.error.type === CLEENG_UNSUPPORTED_REGION_ERROR_TYPE
        }
      />
    );
  }

  if (isLoading || isNullish(data)) {
    return <LogoLoadingSpinnerCentered />;
  }

  if (shouldShowPurchase) {
    return (
      <ThemeProvider theme={themeColor}>
        <Provider store={store}>
          <OTTPurchaseWrapper>
            {shouldShowInfoMessage && (
              <OTTInfoMessage as={BodyText} size={FontSize.BodyText_8}>
                <span>&#9432; </span>
                {t("OTT_PURCHASE_INFO_MESSAGE")}&nbsp;
                <LinkWithLang
                  style={{ color: "blue" }}
                  to={TERMS_AND_CONDITIONS_URL}
                  target="_blank"
                  locale={locale}
                  isClientSide={false}
                >
                  {t("OTT_PURCHASE_INFO_LINK")}
                </LinkWithLang>
              </OTTInfoMessage>
            )}
            <Purchase
              adyenConfiguration={{
                paymentMethodsConfiguration: {
                  styles: styleObject,
                  card: {
                    styles: styleObject,
                  },
                },
              }}
              offerId={data.data.offer.reference_name}
              onSuccess={() =>
                navigate({
                  pathname: `/${locale}${AppRoutes.ThankYou}`,
                  search: createSearchParams({
                    offerId: params.offerId ?? "",
                    env: env ?? "",
                    redirectUrl,
                  }).toString(),
                })
              }
            />
          </OTTPurchaseWrapper>
        </Provider>
      </ThemeProvider>
    );
  }

  return null;
};
